import React from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';

import { TimeZoneEnums } from '@nhl/nhl-breakaway-ui';

import { GAME_TIME } from '~/core/dateTimeFormats';
import { parseValueFromLocalizedAttribute } from '~/core/utils';
import { CommonGCGame } from '~/views/pages/dotcom/gameCenter/types';

// The date string being passed into this function doesn't include any timezone information - it is just, eg, 2023-10-02.
// Luxon will interpret this as meaning this date is in the system time zone. Setting it to America/New York after creation
// could, as a result, actually cause the date to be misrepresented, as a game at 10pm in New York might be 2am in a
// different timezone. So, converting to New York would make it seem as if the game happened on the prior date.
//
// All this is to say, we just don't manipulate the date at all to account for timezones, since the date will
// be correct without us having to do anything to adjust it.
export const formatDate = (dateTime: string): string => DateTime.fromISO(dateTime).toLocaleString(DateTime.DATE_SHORT);
export const formatTime = (dateTime: string): string =>
    DateTime.fromISO(dateTime).setZone(TimeZoneEnums.AMERICA_NEW_YORK).toLocaleString(GAME_TIME);

export const getScoreboardDescriptionFromGame = (game: CommonGCGame, isPregame: boolean) => {
    const homeTeamPlaceName = game.homeTeam.placeName && parseValueFromLocalizedAttribute(game.homeTeam.placeName);
    const awayTeamPlaceName = game.awayTeam.placeName && parseValueFromLocalizedAttribute(game.awayTeam.placeName);
    const homeTeamName = game.homeTeam.name && parseValueFromLocalizedAttribute(game.homeTeam.name);
    const awayTeamName = game.awayTeam.name && parseValueFromLocalizedAttribute(game.awayTeam.name);
    const gameDate = formatDate(game.gameDate);
    const gameStartTime = formatTime(game.startTimeUTC);
    const homeTeamScore = game.homeTeam.score;
    const awayTeamScore = game.awayTeam.score;

    if (isPregame) {
        // "Pittsburgh Penguins, Boston Bruins, 8:00 PM EST, January 4, 2024"
        return (
            <FormattedMessage
                defaultMessage="{homeTeamPlaceName} {homeTeamName}, {awayTeamPlaceName} {awayTeamName}, {gameDate}, {gameStartTime}"
                description="Description with game date, time, and team info meant to give a quick overview on a future game, i.e. 'Pittsburgh Penguins, Boston Bruins, 8:00 PM EST, January 4, 2024'"
                values={{ homeTeamPlaceName, homeTeamName, awayTeamPlaceName, awayTeamName, gameDate, gameStartTime }}
            />
        );
    }
    // this will be read as e.g. "Pittsburgh Penguins 6, Boston Bruins 5, January 4, 2024"
    return (
        <FormattedMessage
            defaultMessage="{homeTeamPlaceName} {homeTeamName} {homeTeamScore}, {awayTeamPlaceName} {awayTeamName} {awayTeamScore}, {gameDate}"
            description="Description with game date, team, and score info meant to give a quick overview on an ongoing or concluded game, i.e. 'Pittsburgh Penguins 6, Boston Bruins 5, January 4, 2024'"
            values={{
                homeTeamPlaceName,
                homeTeamName,
                homeTeamScore,
                awayTeamPlaceName,
                awayTeamName,
                awayTeamScore,
                gameDate,
            }}
        />
    );
};

export const getScoreboardBorderRadius = (isExpanded?: boolean, isShowingGameLinks?: boolean): string => {
    // no border radius when scoreboard is expanded
    if (isExpanded) {
        return '0';
    }

    // only the top 2 corners have border radius when game links section is underneath;
    if (isShowingGameLinks) {
        return '4px 4px 0 0';
    }

    // otherwise, all corners have border radius
    return '4px';
};

export const getScoreboardDecoratorBorderRadius = (
    isExpanded?: boolean,
    isHome?: boolean,
    isShowingGameLinks?: boolean
): string => {
    // when expanded, don't show any border radius
    if (isExpanded) {
        return '0';
    }

    // when not expanded but not showing game links, apply border radius to all of the corners
    if (!isShowingGameLinks) {
        return '3px';
    }

    // when game links are being shown, apply border radius to different corners depending whether on the home or away side
    return isHome ? '0 3px 0 0' : '0 0 3px 0';
};
